import { useTranslation } from 'react-i18next';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { defaultLocale, GeneratedLocale, localeResourceLoaders } from '@/generated/i18n-resources/loaders';
import { createLogger } from '@/shared/logging';

type TranslationBundle = Record<string, Record<string, unknown>>;
type TranslationStore = {
	addResourceBundle(locale: string, namespace: string, resources: Record<string, unknown>, deep: boolean, overwrite: boolean): void;
};

interface ClientTranslationsProps {
	children: ReactNode;
	locale?: string;
}

const translationLog = createLogger('i18n');
const installedLocales = new WeakMap<TranslationStore, Set<GeneratedLocale>>();
const installationPromises = new WeakMap<TranslationStore, Map<GeneratedLocale, Promise<void>>>();

function normaliseLocale(locale: string | undefined): GeneratedLocale {
	const exact = locale as GeneratedLocale;
	if (Object.hasOwn(localeResourceLoaders, exact)) return exact;

	const base = locale?.split('-')[0] as GeneratedLocale;
	return Object.hasOwn(localeResourceLoaders, base) ? base : defaultLocale;
}

function installBundle(store: TranslationStore, locale: GeneratedLocale, bundle: TranslationBundle): void {
	for (const [namespace, resources] of Object.entries(bundle)) {
		store.addResourceBundle(locale, namespace, resources, true, true);
	}
}

function isInstalled(store: TranslationStore, locale: GeneratedLocale): boolean {
	return installedLocales.get(store)?.has(locale) ?? false;
}

async function ensureLocaleInstalled(store: TranslationStore, locale: GeneratedLocale): Promise<void> {
	if (isInstalled(store, locale)) return;

	let storePromises = installationPromises.get(store);
	if (!storePromises) {
		storePromises = new Map();
		installationPromises.set(store, storePromises);
	}

	const inFlight = storePromises.get(locale);
	if (inFlight) {
		await inFlight;
		return;
	}

	const installation = localeResourceLoaders[locale]()
		.then((bundle) => {
			installBundle(store, locale, bundle);
			const locales = installedLocales.get(store) ?? new Set<GeneratedLocale>();
			locales.add(locale);
			installedLocales.set(store, locales);
		})
		.finally(() => storePromises?.delete(locale));

	storePromises.set(locale, installation);
	await installation;
}

/**
 * Installs the active locale and the English fallback once per browser session.
 * Routes can then render from the client i18n store without data requests.
 */
export function ClientTranslations({ children, locale }: ClientTranslationsProps) {
	const { i18n } = useTranslation(undefined, { useSuspense: false });
	const activeLocale = useMemo(() => normaliseLocale(locale), [locale]);
	const [readyLocale, setReadyLocale] = useState<GeneratedLocale | null>(() => (isInstalled(i18n, activeLocale) ? activeLocale : null));

	useEffect(() => {
		let active = true;
		const requiredLocales: GeneratedLocale[] = activeLocale === defaultLocale ? [defaultLocale] : [defaultLocale, activeLocale];

		Promise.all(requiredLocales.map((requiredLocale) => ensureLocaleInstalled(i18n, requiredLocale)))
			.then(async () => {
				if (!active) return;
				if (i18n.language !== activeLocale) await i18n.changeLanguage(activeLocale);
				if (!active) return;
				document.documentElement.lang = activeLocale;
				setReadyLocale(activeLocale);
			})
			.catch((error: unknown) => {
				translationLog.error('client translations failed to load', {
					error,
					locale: activeLocale
				});
				// A missing translation bundle must not make the whole application unusable.
				// i18next will show keys for the missing values, which is diagnosable and recoverable.
				if (active) setReadyLocale(activeLocale);
			});

		return () => {
			active = false;
		};
	}, [activeLocale, i18n]);

	return readyLocale === activeLocale ? children : null;
}
